import { zodResolver } from '@hookform/resolvers/zod';
import { CaretDown, CaretUp } from '@nike/nike-design-system-icons';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';

import BankDepositConfirmationModal from './BankDepositConfirmationModal';
import { getSchema, getVarianceReason } from './validation';
import { createValidationConfig } from './validationConfig';
import { PATH_SAFE_RECONCILIATION } from '../../../../constants/routes.const';
import {
  useGetStoreInfo,
  useGetBankDepositCount,
  useSetGlobalBankDepositCount,
  useGetProfile,
  useGetLangLocale,
} from '../../../../globals';
import { useGetLocalization } from '../../../../hooks';
import {
  useBankDepositMutation,
  useGetDepositAmountFromStoreIDQuery,
  useGetExistingDepositSlipsQuery,
} from '../../../../redux/services/store/storeService';
import { getFormattedCurrency } from '../../../../utils/currencyUtils';
import {
  ID_DEPOSIT_SLIP,
  ID_PROCEED_TO_SAFE_RECONCILIATION_BUTTON,
} from '../../../../utils/dom-element-ids';
import { getDirection } from '../../../../utils/getDirection';
import { TextField, TextFieldWithPlaceholder, MultipleCurrencyInput } from '../../../common';
import { FormWrapper } from '../../../common/FormWrapper';
import { ButtonSubmit, Subheader, Table, VarianceReasonModal } from '../../../reusable';

const OpenOrClose = ({ OpenIcon, CloseIcon }) => {
  function OpenOrCloseFunction({ isOpen }) {
    return isOpen ? <CloseIcon /> : <OpenIcon />;
  }
  OpenOrCloseFunction.propTypes = {
    isOpen: PropTypes.bool.isRequired,
  };
  return OpenOrCloseFunction;
};

const getVarianceAmount = (actualAmount, expectedAmount) => actualAmount - expectedAmount;

function OpenTableItem({ item }) {
  const { locale } = useGetStoreInfo();
  const getLocalization = useGetLocalization();
  const { userLang } = useGetLangLocale();
  const direction = getDirection(userLang);

  const {
    control,
    originalDepositAmount,
    currencyCode,
    subTenderType,
    index,
    setValue,
    depositAmountLabel,
    depositSlipLabel,
    varianceReasonDefinitionsLabel,
    varianceReasonLabel,
    varianceExplanationLabel,
  } = item;

  const validationConfig = createValidationConfig(getLocalization, currencyCode);

  const watchedDepositAmount = useWatch({
    control,
    name: `items.${index}.depositAmount`,
  });

  const varianceAmount = getVarianceAmount(watchedDepositAmount, originalDepositAmount);

  useEffect(() => {
    const varianceReason = getVarianceReason(varianceAmount, validationConfig.variance).VALUE;
    setValue(`items.${index}.varianceReason`, varianceReason);
  }, [
    watchedDepositAmount,
    originalDepositAmount,
    validationConfig.variance,
    varianceAmount,
    setValue,
    index,
  ]);

  return (
    <>
      <TextFieldWithPlaceholder
        control={control}
        id={`items.${index}.${ID_DEPOSIT_SLIP}`}
        label={depositSlipLabel}
        name={`items.${index}.depositSlip`}
        requiredIndicator="*"
        placeholder={getLocalization('lbl.type-bank-deposit-slip-number')}
        direction={direction}
        shouldMakeActive
      />
      <MultipleCurrencyInput
        isRequired
        allowNegativeValue={false}
        control={control}
        id={`items.${index}.depositAmount`}
        label={depositAmountLabel}
        name={`items.${index}.depositAmount`}
        supportText={`${getLocalization('lbl.variance-amount')}: ${getFormattedCurrency(
          locale,
          subTenderType || currencyCode,
          varianceAmount,
        )}`}
        subTenderType={subTenderType}
      />
      <div className="variance-reason-row pb3-sm">
        <TextField
          readOnly
          control={control}
          id={`items.${index}.varianceReason`}
          label={varianceReasonLabel}
          name={`items.${index}.varianceReason`}
          direction={direction}
        />
        <div className="pb3-sm">
          <VarianceReasonModal
            className="dialogModal"
            modalTitle={varianceReasonDefinitionsLabel}
          />
        </div>
      </div>
      <TextField
        control={control}
        shouldMakeActive
        type="string"
        name={`items.${index}.varianceExplanation`}
        id={`items.${index}.varianceExplanation`}
        label={varianceExplanationLabel}
        direction={direction}
      />
    </>
  );
}

OpenTableItem.propTypes = {
  item: PropTypes.shape({
    index: PropTypes.number.isRequired,
    isDisabled: PropTypes.bool.isRequired,
    control: PropTypes.shape().isRequired,
    isOpenable: PropTypes.bool.isRequired,
    originalDepositAmount: PropTypes.number.isRequired,
    currencyCode: PropTypes.string.isRequired,
    subTenderType: PropTypes.string,
    setValue: PropTypes.func.isRequired,
    depositAmountLabel: PropTypes.string.isRequired,
    depositSlipLabel: PropTypes.string.isRequired,
    varianceReasonDefinitionsLabel: PropTypes.string.isRequired,
    varianceReasonLabel: PropTypes.string.isRequired,
    varianceExplanationLabel: PropTypes.string.isRequired,
  }).isRequired,
};

export function BankDeposit() {
  const location = useLocation();
  const { isEndOfDayProcess = false } = location.state || {};

  const getLocalization = useGetLocalization();
  const { currency, locale } = useGetStoreInfo();
  const { storeId, storeNumber } = useGetProfile();
  const validationConfig = createValidationConfig(getLocalization, currency, storeNumber);
  const { userLang } = useGetLangLocale();
  const direction = getDirection(userLang);

  const {
    data: { deposits = [] } = {},
    isLoading: isGetDepositAmountLoading,
    error: getDepositAmountError,
  } = useGetDepositAmountFromStoreIDQuery({
    safeId: `${storeId}-888-${currency}`,
    storeId,
    currency,
  });

  const {
    data: existingDepositSlips,
    error: getExistingDepositSlipsError,
    isLoading: isGetExistingDepositSlipsLoading,
  } = useGetExistingDepositSlipsQuery();

  const form = useForm({
    resolver: zodResolver(getSchema(validationConfig, existingDepositSlips)),
    defaultValues: {
      items: deposits,
    },
    shouldUnregister: false,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const {
    control,
    // eslint-disable-next-line no-unused-vars
    formState: { isSubmitSuccessful, errors },
    register,
    setValue,
    reset,
  } = form;

  useEffect(() => {
    if (deposits.length > 0) {
      reset({ items: deposits });
    }
  }, [deposits, reset]);

  const [
    bankDeposit,
    { error: submitError, isSuccess: isSubmitSuccess, isLoading: isSubmitLoading },
  ] = useBankDepositMutation();

  const replace = useNavigate();
  const submit = () => replace(PATH_SAFE_RECONCILIATION, { state: { isEndOfDayProcess } });

  const bankDepositCount = useGetBankDepositCount();
  const setBankDepositCount = useSetGlobalBankDepositCount();

  const handleSubmit = (data) => {
    const bankRequest = {
      currencyCode: currency,
      deposits: data.items.map((item) => ({
        amountBackToSafe: item.amountBackToSafe,
        currencyCode: item.currencyCode,
        // if foreign tender deposit amount is updated by user, converted amount is not updated
        ...(item.isForeignTender && { convertedAmount: item.convertedAmount }),
        depositAmount: item.depositAmount,
        depositSlip: item.depositSlip,
        variance: item.originalDepositAmount - item.depositAmount,
        expectedDepositAmount: item.originalDepositAmount,
        isForeignTender: item.isForeignTender,
        tenderType: item.tenderType,
        ...(item?.subTenderType && { subTenderType: item?.subTenderType }),
        varianceExplanation: item?.varianceExplanation,
        varianceReason: item.varianceReason,
      })),
    };

    bankDeposit(bankRequest);
    setBankDepositCount(bankDepositCount + 1);
  };

  return (
    <article className="va-sm-t ta-sm-c ncss-col-sm-12 ncss-col-md-6 ncss-col-lg-4 p4-sm">
      <Subheader>{getLocalization('lbl.bank-deposit')}</Subheader>
      <FormWrapper
        fetchErrors={[getDepositAmountError, getExistingDepositSlipsError]}
        form={form}
        loadingFlags={[isGetDepositAmountLoading, isGetExistingDepositSlipsLoading]}
        submit={{
          error: submitError,
          handler: (data) => handleSubmit(data),
          isLoading: isSubmitLoading,
          isSuccess: isSubmitSuccess,
          label: getLocalization('lbl.deposit-to-bank'),
          disableAfter: true,
        }}
      >
        <Table
          Open={OpenTableItem}
          columns={[
            {
              header: getLocalization('lbl.tender-type'),
              width: 5,
              processor: ({ tenderType, subTenderType }) =>
                tenderType + (subTenderType ? `-${subTenderType}` : ''),
            },
            {
              header: getLocalization('lbl.expected'),
              width: 6,
              processor: ({ originalDepositAmount, subTenderType }) =>
                getFormattedCurrency(locale, subTenderType || currency, originalDepositAmount),
            },
            {
              getClassName: () => 'ta-sm-r',
              headerClassName: 'ta-sm-r',
              processor: OpenOrClose({ CloseIcon: CaretUp, OpenIcon: CaretDown }),
              width: 1,
            },
          ]}
          data={deposits?.map((item, index) => ({
            ...item,
            isOpenable: item.tenderType === 'CASH',
            isDisabled: item.tenderType !== 'CASH',
            control,
            register,
            setValue,
            index,
            subTenderType: item.subTenderType,
            depositSlipLabel: getLocalization('lbl.deposit-slip'),
            depositAmountLabel: getLocalization('lbl.amount'),
            varianceReasonLabel: getLocalization('lbl.variance-reason'),
            varianceExplanationLabel: getLocalization('lbl.variance-explanation'),
            varianceReasonDefinitionsLabel: getLocalization('lbl.variance-reason-definitions'),
          }))}
          showPagination={false}
          title=""
          direction={direction}
        />
      </FormWrapper>
      {isEndOfDayProcess &&
        (bankDepositCount > 0 || !deposits.every(({ depositAmount }) => depositAmount > 0) ? (
          <ButtonSubmit
            id={ID_PROCEED_TO_SAFE_RECONCILIATION_BUTTON}
            label={getLocalization('lbl.proceed-to-safe-reconciliation')}
            onSubmit={submit}
          />
        ) : (
          <BankDepositConfirmationModal modalTitle="" submit={submit} />
        ))}
    </article>
  );
}
